import { useRef, useState, useEffect } from 'react';

const SERVICES = [
  {
    title: 'Custom Wash Bays',
    category: 'Aqueous Cleaning',
    desc: 'Floor-level wash bay systems for large equipment, trucks, and high-volume parts cleaning. Sizes 4\'W x 8\' and up with recirculating wash and filtration.',
    benefit: 'Maximize throughput with custom wash stations built to your facility dimensions.',
    img: 'https://public.readdy.ai/ai/img_res/415e7300992225ca347dcf5a431abf93.jpg',
    tag: 'Most Popular',
    href: 'https://www.kc-qualitysystems.com/all-other-products/wash-bays/',
  },
  {
    title: 'Front Load Cabinet Washers',
    category: 'Cabinet Systems',
    desc: 'MAXJET front-load cabinets with standard turntable diameters of 30", 36", 50", 60", and 72". Extra duty models support up to 40,000 lbs capacity.',
    benefit: 'Industry-standard cabinets engineered for the heaviest industrial applications.',
    img: 'https://readdy.ai/api/search-image?query=industrial%20cabinet%20parts%20washer%20machine%2C%20front%20loading%20industrial%20cleaning%20equipment%2C%20professional%20factory%20setting%2C%20dramatic%20side%20lighting%2C%20close-up%20precision%20industrial%20machinery%2C%20metallic%20surfaces%2C%20clean%20industrial%20environment&width=600&height=400&seq=svc-cabinet-v3&orientation=landscape',
    tag: null,
    href: 'https://www.kc-qualitysystems.com/all-other-products/front-load-washers/',
  },
  {
    title: 'Conveyor Inline Washers',
    category: 'Production Line',
    desc: 'Single and multi-stage conveyor systems with custom belt and overhead conveyor options for continuous production cleaning at scale.',
    benefit: 'Seamlessly integrate precision cleaning into your production line for zero-downtime operations.',
    img: 'https://readdy.ai/api/search-image?query=industrial%20conveyor%20belt%20parts%20washer%20system%20inside%20factory%2C%20automated%20production%20line%20cleaning%20machine%2C%20overhead%20belt%20conveyor%20moving%20metal%20parts%20through%20washing%20stages%2C%20industrial%20facility%20dramatic%20lighting%2C%20professional%20photography&width=600&height=400&seq=svc-conveyor-v3&orientation=landscape',
    tag: 'High Volume',
    href: 'https://www.kc-qualitysystems.com/all-other-products/conveyor-inline/',
  },
  {
    title: 'Dry Blast Cabinets',
    category: 'Surface Finishing',
    desc: 'Wide variety of finishing and polishing solutions — benchtop, standard, and split models available with BP dust collectors or abrasive separators.',
    benefit: 'Achieve bare-metal surface preparation with precision-grade blasting technology.',
    img: 'https://readdy.ai/api/search-image?query=industrial%20sandblast%20cabinet%20machine%2C%20dry%20blast%20surface%20finishing%20equipment%20in%20factory%2C%20close-up%20of%20abrasive%20blasting%20cabinet%20with%20steel%20parts%2C%20dramatic%20industrial%20lighting%2C%20professional%20equipment%20photography&width=600&height=400&seq=svc-blast-v3&orientation=landscape',
    tag: null,
    href: 'https://www.kc-qualitysystems.com/all-other-products/dry-blast-cabinets/',
  },
  {
    title: 'Powder Coat & Cure Ovens',
    category: 'Finishing Systems',
    desc: 'Heavy-duty welded construction with special insulated material. Reaches temperatures up to 500°F for powder curing and burn-off applications.',
    benefit: 'Complete your finishing process with professional-grade oven systems rated to 500°F.',
    img: 'https://readdy.ai/api/search-image?query=industrial%20powder%20coat%20curing%20oven%20large%20commercial%20finishing%20oven%20interior%2C%20metal%20parts%20hanging%20inside%20powder%20coating%20facility%2C%20dramatic%20orange%20and%20warm%20light%20from%20oven%2C%20professional%20industrial%20photography&width=600&height=400&seq=svc-oven-v3&orientation=landscape',
    tag: null,
    href: 'https://www.kc-qualitysystems.com/all-other-products/gas-electric-cure-ovens/',
  },
  {
    title: 'Immersion Parts Washers',
    category: 'Deep Clean',
    desc: 'GrayMills & MAXJET immersion cleaning systems for processes requiring extended soaking times — parts placed in baskets within recirculating cleaning solution.',
    benefit: 'Deep-clean complex geometries and intricate parts with total immersion technology.',
    img: 'https://readdy.ai/api/search-image?query=industrial%20immersion%20parts%20washer%20tank%2C%20metal%20parts%20submerged%20in%20cleaning%20solution%2C%20industrial%20cleaning%20equipment%20with%20lift%20mechanism%2C%20factory%20setting%20with%20dramatic%20lighting%2C%20professional%20industrial%20machinery%20photography&width=600&height=400&seq=svc-immersion-v3&orientation=landscape',
    tag: null,
    href: 'https://www.kc-qualitysystems.com/all-other-products/immersion-parts-washers-2/',
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function ServicesSection() {
  const { ref, inView } = useInView();

  return (
    <section className="bg-[#0d0d0d] py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">

        {/* Section header */}
        <div className={`flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <span className="text-gold text-xs font-semibold tracking-[0.4em] uppercase font-sans">What We Offer</span>
            <h2 className="font-serif text-4xl lg:text-5xl text-white mt-4 leading-tight">
              Complete Quality &amp;<br />Process System Solutions
            </h2>
          </div>
          <p className="text-white/40 font-sans max-w-sm text-base leading-relaxed lg:text-right">
            From custom wash bays to conveyor inline systems — we build and deliver MAXJET aqueous cleaning solutions engineered around your exact operation.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((svc, idx) => (
            <a
              key={idx}
              href={svc.href}
              rel="nofollow"
              className={`group relative bg-[#0a0a0a] rounded-lg overflow-hidden border border-[#1e1e1e] hover:border-gold/30 transition-all duration-500 flex flex-col cursor-pointer ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${idx * 90}ms` }}
            >
              {/* Image */}
              <div className="h-52 overflow-hidden relative bg-steel">
                <img
                  src={svc.img}
                  alt={svc.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/20 to-transparent" />
                {/* Category badge */}
                <div className="absolute top-4 left-4 px-3 py-1 bg-black/60 backdrop-blur-sm border border-white/10 rounded-full">
                  <span className="text-white/60 text-[10px] font-sans tracking-widest uppercase">{svc.category}</span>
                </div>
                {svc.tag && (
                  <div className="absolute top-4 right-4 px-3 py-1 bg-gold/15 border border-gold/30 rounded-full">
                    <span className="text-gold text-[10px] font-bold font-sans tracking-wide">{svc.tag}</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-serif text-xl text-white mb-3 group-hover:text-gold/90 transition-colors">{svc.title}</h3>
                <p className="text-white/40 text-sm font-sans leading-relaxed mb-4 flex-1">{svc.desc}</p>

                {/* Benefit */}
                <div className="flex items-start gap-2 mb-5 pt-4 border-t border-[#1e1e1e]">
                  <i className="ri-check-line text-gold text-sm mt-0.5 flex-shrink-0" />
                  <p className="text-white/60 text-xs font-sans leading-relaxed">{svc.benefit}</p>
                </div>

                <div className="flex items-center gap-2 text-gold text-sm font-semibold font-sans group-hover:gap-3 transition-all whitespace-nowrap">
                  Explore System
                  <i className="ri-arrow-right-line" />
                </div>
              </div>

              {/* Bottom gold line on hover */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </a>
          ))}
        </div>

        {/* Additional product tags */}
        <div className={`mt-10 flex flex-wrap justify-center gap-2.5 transition-all duration-700 delay-600 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {[
            { label: 'Top Loading Washers', href: 'https://www.kc-qualitysystems.com/all-other-products/top-loading/' },
            { label: 'Ultrasonic Tanks', href: 'https://www.kc-qualitysystems.com/all-other-products/ultrasonic-tanks/' },
            { label: 'Vapor Degreasers', href: 'https://www.kc-qualitysystems.com/all-other-products/vapor-degreaser/' },
            { label: 'Aerospace Systems', href: 'https://www.kc-qualitysystems.com/all-other-products/aerospace/' },
            { label: 'Airless Shot Blasters', href: 'https://www.kc-qualitysystems.com/all-other-products/airless-blast-cabinets/' },
            { label: 'High Pressure Hand Spray', href: 'https://www.kc-qualitysystems.com/all-other-products/high-pressure-hand-spray/' },
          ].map((tag) => (
            <a
              key={tag.label}
              href={tag.href}
              rel="nofollow"
              className="px-5 py-2 border border-[#1e1e1e] rounded-full text-white/30 text-xs font-sans hover:border-gold/35 hover:text-gold/80 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {tag.label}
            </a>
          ))}
        </div>

        {/* Mid-page CTA */}
        <div className={`mt-16 transition-all duration-700 delay-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="relative rounded-xl overflow-hidden border border-[#1e1e1e] bg-gradient-to-r from-[#0d0d0d] to-[#111]">
            {/* Decorative gold gradient */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
            <div className="px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <p className="text-white/40 text-xs font-sans tracking-widest uppercase mb-2">Not Sure What You Need?</p>
                <h3 className="font-serif text-2xl text-white">Let Our Engineers Find the Right System</h3>
                <p className="text-white/40 text-sm font-sans mt-2 max-w-xl">
                  Tell us about your parts, your volumes, and your process — we'll match you to the exact system that fits.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 flex-shrink-0">
                <a
                  href="#contact"
                  className="px-8 py-4 bg-gold text-black font-bold text-sm rounded-md hover:bg-gold-light transition-colors cursor-pointer font-sans whitespace-nowrap"
                >
                  Start a Consultation
                </a>
                <a
                  href="tel:8665407575"
                  className="px-8 py-4 border border-[#2a2a2a] text-white/60 font-semibold text-sm rounded-md hover:border-gold/40 hover:text-gold transition-colors cursor-pointer font-sans whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-phone-line text-gold" /> 866-540-7575
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
